import { keepPreviousData, useQuery, queryOptions } from '@tanstack/react-query';

import { STALE_TIMES } from '#lib/queryConstants';

import {
	fetchCampaigns,
	fetchCampaign,
	fetchSends,
	fetchSendsStats,
	fetchBlockedRecipients,
	fetchCostEstimate,
} from '../../api/postMailingApi';

import { postMailingCampaignsKeys } from './keys';

export const postMailingCampaignsListOptions = () =>
	queryOptions({
		queryKey: postMailingCampaignsKeys.list(),
		queryFn: fetchCampaigns,
		staleTime: STALE_TIMES.FREQUENT,
	});

export function usePostMailingCampaigns() {
	return useQuery(postMailingCampaignsListOptions());
}

// Transient statuses where the campaign is expected to change soon via a backend webhook (payment
// clearing, send starting). While in one of these, the detail view polls so the UI updates on its own.
const POLLING_STATUSES = new Set([
	'awaiting_payment',
	'payment_processing',
	'sending',
	'ready_to_distribute',
	'needs_review',
]);

export function usePostMailingCampaign(id) {
	return useQuery({
		queryKey: postMailingCampaignsKeys.detail(id),
		queryFn: () => fetchCampaign(id),
		enabled: Boolean(id),
		staleTime: STALE_TIMES.STANDARD,
		// Poll only while the campaign is in a transient state; stop once it settles.
		refetchInterval: (query) => (POLLING_STATUSES.has(query.state.data?.status) ? STALE_TIMES.FREQUENT : false),
	});
}

export function usePostMailingSends(campaignId, { filters = {}, page = 1, pageSize = 50, enabled = true } = {}) {
	// No polling: physical letter status changes over days, not in real time.
	// The statistics tab loads sends once and offers a manual refresh button.
	return useQuery({
		queryKey: postMailingCampaignsKeys.sends(campaignId, filters, page, pageSize),
		queryFn: () => fetchSends(campaignId, { filters, page, pageSize }),
		enabled: Boolean(campaignId) && enabled,
		placeholderData: keepPreviousData,
	});
}

export function usePostMailingSendsStats(campaignId, { enabled = true } = {}) {
	return useQuery({
		queryKey: postMailingCampaignsKeys.sendsStats(campaignId),
		queryFn: () => fetchSendsStats(campaignId),
		enabled: Boolean(campaignId) && enabled,
	});
}

export function usePostMailingBlockedRecipients(campaignId, { enabled = true } = {}) {
	return useQuery({
		queryKey: postMailingCampaignsKeys.blockedRecipients(campaignId),
		queryFn: () => fetchBlockedRecipients(campaignId),
		enabled: Boolean(campaignId) && enabled,
		staleTime: STALE_TIMES.STANDARD,
	});
}

export function usePostMailingCostEstimate(campaignId, { enabled = false } = {}) {
	return useQuery({
		queryKey: postMailingCampaignsKeys.costEstimate(campaignId),
		queryFn: () => fetchCostEstimate(campaignId),
		enabled: Boolean(campaignId) && enabled,
		// Always refetch when the cost modal opens: the response decides between the price/checkout and
		// the manual-offer dialog (`offerRequired`), and the user may have just swapped the templates
		staleTime: 0,
	});
}
